import type { LaddersStyle } from "@bing/engine-ladders";
import {
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react";
import { BOARD_ASPECT, FRAME, SQUARE } from "./geometry";
import { coverScale, pickVariant, SPIRE_CLEAR, warmedVariant, type SceneVariant } from "./prefetch";
import { ScenePicture } from "./ScenePicture";
import { lookFor, type LaddersLook } from "./styles";

/* ------------------------------------------------------------------ *
 * مار پله — the SCENE (card-86; round 2, batch W1): the look's backdrop
 * (a picture, or «اورجینال»'s plain one), the board CENTRED on it, the
 * die's place right above the board. W2 supplies the board (`renderBoard`), the die (`renderDie`) and
 * the strip under it (`footer`). The Bing Games brand is GONE (name and
 * logo — the operator, round 2 item 4).
 *
 * ⚠️ MEASURED, NEVER VIEWPORT-ANCHORED. One ResizeObserver on the scene and
 * the stage feeds ONE pure function, `sceneLayout`, that places the board,
 * then the die, then the picture.
 *
 * ROUND 3 (card-86-r3b, the operator 2026-11-04: «Both background pictures
 * look heavily zoomed in and lose their quality, and parts of the scene are
 * cut off … Never stretch a picture beyond its real resolution. If a
 * screen's shape doesn't match the picture, fill the edges in a way that
 * blends with it instead of cropping the scene»). The picture is no longer
 * raised to COVER: it is CONTAINED — the whole picture, uncropped — in the
 * scene under the top inset (so the temple / pavilion is never under
 * Telegram's header), at most at its widest variant's real px, and drawn
 * from the variant whose px cover the box's device px (`pickVariant`). The
 * rest of the scene — the header strip, a wider screen's sides — is the
 * FILL: the picture itself, blurred at build time with its edges carried
 * outward, stretched by a static `border-image` so its centre lies exactly
 * under the picture and its margins reach every edge (the full-bleed of
 * round 2 item 5, with no white strip and no crop); the picture's edges
 * feather into it. The board's size is today's (the solver is unchanged);
 * it is only lowered, within the slack, to start under the landmark's foot,
 * and the picture slides sideways inside its fill until the spire stands
 * over the 100 cell ({@link PICTURE_SLIDES_TO_100}). Nothing of the picture
 * is drawn over the board any more (round 2's cut-out is gone).
 *
 * ROUND 5 (the operator, 2026-11-04: «On prod, a large part of the background looks blurred: those
 * are the blended edge bands. Fill the screen with the real, sharp picture instead. Crop a little at
 * the edges where the screen's shape differs, as long as the temple or pavilion and the scene's main
 * frame stay fully visible and nothing is stretched. No large blurred areas»; their answers: the
 * temple fully visible — the strip behind Telegram's header keeps the soft fill —, Telegram Desktop
 * filled too, the board smaller there if it must). The picture now COVERS the scene under the top
 * inset ({@link coverScale}): the full width and down to the bottom edge, its spire's tip
 * {@link SPIRE_CLEAR} px under the inset, cropped at the right and the bottom only, never past its
 * widest variant's real px. The board starts under the landmark's foot ALWAYS — a hard rule now: on a
 * wide window the side gives way, never the picture. And «bring the die down, so it sits where the
 * thumb reaches more easily»: the die's place goes UNDER the player strip ({@link SceneLayout.dieBelow})
 * wherever that costs the board nothing (the operator: «Keep the board's size there») — else it stays
 * above the board, beside the temple, as before.
 *
 * ⚠️ NOTHING IS MIRRORED: `dir="ltr"` on the scene, physical left/top
 * only. 100 stays top-left under the temple (a CSS grid would follow
 * `dir="rtl"` and move it top-right, away from the temple).
 *
 * ⚠️ THE SCENE OWNS THE SAFE AREAS (top and bottom insets): the footer
 * must not pad them again. The scene itself is inset 0 — under Telegram's
 * header buttons — and only its column is padded.
 *
 * ⚠️ NOTHING HERE MOVES: no animation, no transition, no runtime blur. The
 * picture's load fade is `ScenePicture`'s (batch P), once.
 * ------------------------------------------------------------------ */

/** What the scene tells its footer: the column's width in CSS px (0 before the first measure).
 *  Measured from the stage, so it never depends on the footer's own height. */
export interface LaddersSceneFit {
  readonly width: number;
}

export interface LaddersSceneProps {
  /** `view.style` — the look (an unknown one draws jungle). */
  readonly style: LaddersStyle;
  /** The board, drawn in the centred board slot at `sizePx` (the square's side). */
  readonly renderBoard: (sizePx: number) => ReactNode;
  /** The die, drawn in its place right ABOVE the board, centred on it: a `sizePx` square, ≥ 44 —
   *  the tap target; `draw` says how big to DRAW the die in it and how far to raise the drawing. */
  readonly renderDie: (sizePx: number, draw?: DieDraw) => ReactNode;
  /** W2: seat strip, hint, timer — or a function of the column's fit. */
  readonly footer: ReactNode | ((fit: LaddersSceneFit) => ReactNode);
  /** W2: the shell's overlays (exit, chat toggle). */
  readonly children?: ReactNode;
}

/** The board's square never runs closer than this to the column's sides. */
export const SCENE_MARGIN_X = 8;
/** Room between the board (its start pad) and the footer. */
export const SCENE_GAP_BELOW = 6;
/** The board's largest side, on any screen. */
export const SCENE_MAX_SIDE = 520;
/**
 * The top of the stage that W2's header strip owns — the exit and the chat toggle, 40 px buttons
 * 10 px under the safe top inset (as on every table) — in STAGE px: the stage starts 8 px under the
 * inset, so the strip ends at 10 + 40 − 8 = 42, plus a 4 px gap. The die never enters it (at
 * 240×533 the centred die would sit on the chat toggle). Keep in step with W2's strip.
 */
export const SCENE_HEADER_RESERVE = 10 + 40 - 8 + 4;
/** The table column's width (every table's: `max-w-[440px]`). */
export const SCENE_COLUMN_MAX = 440;
/** The die's place: never smaller than this (a thumb's target)… */
export const DIE_SLOT_MIN = 44;
/** …never larger than this, and otherwise this share of the board's side. */
export const DIE_SLOT_MAX = 52;
export const DIE_SLOT_SHARE = 0.13;
/** Between the die's place and the board's frame — at least this; a taller screen gives more. */
export const DIE_GAP = 6;
/**
 * Of the room ABOVE the board (`SLACK_ABOVE_*`), this share goes between the die and the board —
 * the rest between the header strip and the die (the operator, 2026-11-04: «Move the die's area a
 * bit higher, so there's more space between it and the board»).
 */
export const DIE_GAP_SHARE = 0.5;
/** The die stays at least this far right of the temple's box. */
export const DIE_TEMPLE_GAP = 4;
/**
 * Of a tall screen's spare height, this share goes ABOVE the die and the board, the rest UNDER the
 * player strip — the die, the board and the strip stand together, centred…
 */
export const SLACK_ABOVE_SHARE = 0.5;
/** …but never more than this above — the more room above the board, the more the picture grows. */
export const SLACK_ABOVE_MAX = 56;
/**
 * The player strip (the scene's `footer`) sits this far under the board's start pad — raised to
 * it, never left at the bottom of a tall screen (the operator, 2026-11-04: «Move the player panel
 * at the bottom … a bit closer to the board»). Its height is its own (fixed during play).
 */
export const FOOTER_GAP = SCENE_GAP_BELOW;
/** The column's own top padding over the safe inset (CSS px): the stage starts this far under it. */
export const SCENE_PAD_TOP = 8;
/**
 * P3 of round 3 — «keep the 100 cell right under the temple/pavilion»: the contained picture slides
 * sideways inside its fill until the spire's x is over the 100 cell's centre, as far as the fill's
 * room allows (the picture is never cropped). On a phone that is all the room there is: the picture
 * meets the screen's left edge and the whole fill strip is on the right; the spire lands inside the
 * 100 cell on every size checked. `false` centres the picture instead (the spire then stands over
 * the 99 cell on the phones).
 */
export const PICTURE_SLIDES_TO_100 = true;
/**
 * Where the picture meets its fill, its own edge fades into it over this many CSS px (a static mask)
 * — at the top never deeper than the band above the landmark's tip (`band.top`), so the spire is
 * never faded.
 */
export const FEATHER_PX = 16;
/** Round 5: the die's place under the player strip sits this far below it — the same as the strip
 *  under the board, so a die below costs exactly what a die above does ({@link sceneLayout}). */
export const DIE_BELOW_GAP = FOOTER_GAP;
/**
 * Round 5 (the operator, for windows bigger than Telegram Desktop's defaults: «Cap at ~20%»): a
 * covering picture may cost the board at most this share of its side (against the board the screen
 * fits with no landmark at all). Where it would cost more, the picture stops growing there — narrow
 * soft strips come back at the sides of that window only.
 */
export const BOARD_SHRINK_MAX = 0.2;
/**
 * …and the board may give up at most this share of its side for the die to go under the strip —
 * the few px a four-seat strip costs on the common phones (≤ 2.2 %: 390×844, 393×852, 375×812 in
 * «جنگل»); a short screen, which would lose 10–20 %, keeps its board and its die above (the
 * operator: «Keep the board's size there»).
 */
export const DIE_BELOW_SHRINK_MAX = 0.03;

export interface SceneBox {
  readonly left: number;
  readonly top: number;
  readonly width: number;
  readonly height: number;
}

/** What `sceneLayout` is measured against — CSS px. */
export interface SceneFrame {
  /** The stage: the column between the padded top and the footer. */
  readonly stageW: number;
  readonly stageH: number;
  /** The stage's top-left inside the scene (the full-bleed root). */
  readonly offX: number;
  readonly offY: number;
  /** The whole scene (the viewport, header strip included). */
  readonly sceneW: number;
  readonly sceneH: number;
  /** Device px per CSS px (`devicePixelRatio`): the picture never gets more than its variant has. */
  readonly dpr: number;
}

/** How far each edge of the scene lies past the picture (CSS px, ≥ 0): the fill's border widths. */
export interface SceneGaps {
  readonly top: number;
  readonly right: number;
  readonly bottom: number;
  readonly left: number;
}

export interface SceneLayout {
  /** The board's square side (`sizePx`). */
  readonly side: number;
  /** The board element (square + start pad), in stage px. */
  readonly board: SceneBox;
  /** The die's place (a square): in stage px above the board — or, when {@link dieBelow}, its `left`
   *  in the column's px and its `top` measured down from the player strip's bottom edge. */
  readonly die: SceneBox;
  /** Round 5: the die's place is UNDER the player strip (drawn in the footer, which carries it up). */
  readonly dieBelow: boolean;
  /** How the die is drawn in its place ({@link dieDrawFor}). */
  readonly dieDraw: DieDraw;
  /** Source px → CSS px (0 for a look without a picture). */
  readonly scale: number;
  /** The whole picture, in stage px — round 5: it COVERS the scene under the top inset (its right and
   *  bottom may run past the scene's edges, cropped there); its spire's tip under the inset. */
  readonly bg: SceneBox | null;
  /** The variant drawn: the narrowest whose px cover the box's device px. */
  readonly variant: SceneVariant | null;
  /** Image px per device px of that variant on this screen (≥ 1: never stretched). */
  readonly sharpness: number;
  /** The fill's border widths: from its box ({@link fillBox}) in to the picture — round 5: the
   *  strip behind Telegram's header above it, and past it only where the picture cannot cover. */
  readonly gaps: SceneGaps | null;
  /** The fill's box, in SCENE px: the scene and the picture together, so the fill's centre lies
   *  exactly under the picture even where the picture runs past the scene's edges. */
  readonly fillBox: SceneBox | null;
  /** How deep the picture's edge fades into the fill on each side (0 where it meets the screen's). */
  readonly feather: SceneGaps | null;
  /** The landmark's foot (stage px): the board starts at or under it — always (round 5). */
  readonly footY: number | null;
  /** The temple's box's right edge (stage px) — the die stays clear of it. */
  readonly templeRight: number | null;
  /** How far the footer is RAISED from the column's bottom, to sit {@link FOOTER_GAP} under the
   *  board (CSS px, ≥ 0) — a static transform: the stage, measured, never moves for it. */
  readonly footerLift: number;
}

/** The die's place for a board `side` px wide. */
export function dieSlotFor(side: number): number {
  return Math.round(Math.min(DIE_SLOT_MAX, Math.max(DIE_SLOT_MIN, side * DIE_SLOT_SHARE)));
}

/** How the die is DRAWN inside its place (the place stays the ≥ 44 px tap target). */
export interface DieDraw {
  /** The drawn die's side (CSS px). */
  readonly size: number;
  /** How far the drawing is raised from the place's centre (CSS px, ≥ 0). */
  readonly lift: number;
}
/** The drawn die is this share of its place on most screens… */
export const DIE_DRAW_SHARE = 0.72;
/** …and this share where the board leaves the die no room (the smallest screens). */
export const DIE_DRAW_SHARE_TIGHT = 0.64;
/** Never drawn smaller than this. */
export const DIE_DRAW_MIN = 28;
/**
 * The DRAWN die stands at least this far above the board's frame (the operator, 2026-11-04: «The
 * die's gap is still 6 px on the smallest screens»): where the place's own gap is short, the die
 * is drawn a little smaller and raised inside its place — never out of it, the tap target and the
 * board unchanged. Every screen with room keeps its die exactly as it was.
 */
export const DIE_VISUAL_GAP_MIN = 20;

/** The drawn die for a place `slot` px wide standing `slotGap` px above the board. */
export function dieDrawFor(slot: number, slotGap: number): DieDraw {
  const drawn = (share: number): number => Math.max(DIE_DRAW_MIN, Math.round(slot * share));
  const pad = (size: number): number => (slot - size) / 2;
  const usual = drawn(DIE_DRAW_SHARE);
  if (slotGap + pad(usual) >= DIE_VISUAL_GAP_MIN) return { size: usual, lift: 0 };
  const size = Math.min(usual, drawn(DIE_DRAW_SHARE_TIGHT));
  const want = DIE_VISUAL_GAP_MIN - (slotGap + pad(size));
  return { size, lift: Math.max(0, Math.min(Math.floor(pad(size)), Math.ceil(want))) };
}

/**
 * Where everything goes. Pure: the tests drive it at every device size the operator checks.
 *
 *   1. the largest side such that the header strip, the die's place, the board (+ its start pad)
 *      and the gap above the footer fit the stage height, and the square fits the width — the same
 *      for every look (the picture never costs the board a pixel);
 *   2. the board is CENTRED in the column; a tall screen's spare height goes half above
 *      (≤ {@link SLACK_ABOVE_MAX}) and the rest under the player strip, which is RAISED to sit
 *      {@link FOOTER_GAP} under the board; the die sits above the board, centred on it — the room
 *      above shared between its gap to the board and its gap to the header strip — nudged right
 *      only where the temple's box would touch it;
 *   3. a picture look: the picture CONTAINED in the scene under the top inset (whole, uncropped,
 *      at its top), never past its widest variant's real px; slid sideways inside its fill so the
 *      spire stands over the 100 cell ({@link PICTURE_SLIDES_TO_100}); the board LOWERED, within
 *      the slack only, to start under the landmark's foot — and where that is not enough, the
 *      picture made SMALLER until its foot meets the board's top (the temple / pavilion is always
 *      wholly in view); the fill covers every edge past the picture.
 */
export function sceneLayout(frame: SceneFrame, look: LaddersLook): SceneLayout {
  const { stageW, stageH } = frame;
  // THE PICTURE first (round 5): it covers the scene under the top inset at ONE scale for the frame —
  // never past what a board BOARD_SHRINK_MAX smaller than the screen's own fits under its foot — so
  // the landmark's foot is known before the board is fitted. The foot: bg2's railing; the jungle's
  // stone LEDGE under the temple — its top row (the operator: «Board over the temple's ledge»: the
  // temple building stays whole, only its ledge may be hidden).
  const pic = look.kind === "picture" ? look : null;
  const insetTop = Math.max(0, frame.offY - SCENE_PAD_TOP);
  const solve = (fits: (side: number) => boolean): number => {
    let lo = 0;
    let hi = Math.max(0, Math.min(SCENE_MAX_SIDE, stageW - 2 * SCENE_MARGIN_X));
    if (fits(hi)) lo = hi;
    else {
      for (let i = 0; i < 40; i += 1) {
        const mid = (lo + hi) / 2;
        if (fits(mid)) lo = mid;
        else hi = mid;
      }
    }
    return Math.floor(lo);
  };
  const headAbove = (side: number): number => SCENE_HEADER_RESERVE + dieSlotFor(side) + DIE_GAP;
  /** The largest side whose board starts under the header strip, the die above it (today's budget)
   *  and `foot` (stage px) — and still leaves its start pad above the footer. */
  const sideUnder = (foot: number): number =>
    solve(
      (side) => Math.max(headAbove(side), foot) + side * BOARD_ASPECT + SCENE_GAP_BELOW <= stageH,
    );
  let s = 0;
  let picTop = 0; // stage px
  let footAt = Number.NEGATIVE_INFINITY; // stage px: the board starts at or under it
  if (pic) {
    const S = pic.scene;
    const variants = pic.bg.variants;
    const widest = variants[variants.length - 1]?.w ?? 0;
    const footM = S.temple ? S.temple.y : S.board.y;
    const top0 = insetTop + SPIRE_CLEAR - frame.offY; // where the spire's tip stands (stage px)
    const footFor = (scale: number): number => top0 + (footM - S.band.spireTip) * scale;
    s = coverScale(
      frame.sceneW,
      frame.sceneH,
      insetTop,
      S.master,
      S.band.spireTip,
      widest,
      frame.dpr,
    );
    // The cap: the board a covering picture leaves must be ≥ (1 − BOARD_SHRINK_MAX) of the screen's
    // own; else the picture's scale is the largest that leaves exactly that board under its foot.
    const least = Math.ceil(sideUnder(Number.NEGATIVE_INFINITY) * (1 - BOARD_SHRINK_MAX));
    if (sideUnder(Math.ceil(footFor(s))) < least) {
      const topMax = stageH - SCENE_GAP_BELOW - least * BOARD_ASPECT;
      s = Math.max(0, Math.min(s, (Math.floor(topMax) - top0) / (footM - S.band.spireTip)));
    }
    picTop = top0 - S.band.spireTip * s;
    footAt = footFor(s);
  }
  const foot = Math.ceil(footAt);

  // THE BOARD: the largest side whose board starts under the header strip, the die above it (today's
  // budget), and the landmark's foot — and still leaves its start pad above the footer…
  const minTop = (side: number): number => Math.max(headAbove(side), foot);
  const sideAbove = solve((side) => minTop(side) + side * BOARD_ASPECT + SCENE_GAP_BELOW <= stageH);
  // …and THE DIE UNDER THE STRIP (round 5): the largest side whose board, started under the header
  // strip and the foot, leaves the strip and then the die's place and its gap under it. It is taken
  // when it costs the board at most DIE_BELOW_SHRINK_MAX of its side (never more than today's).
  const underBoard = (side: number): number => FOOTER_GAP + DIE_BELOW_GAP + dieSlotFor(side);
  const sideBelow = solve(
    (side) =>
      Math.max(SCENE_HEADER_RESERVE, foot) + side * BOARD_ASPECT + underBoard(side) <= stageH,
  );
  const dieBelow = sideBelow > 0 && sideBelow >= sideAbove * (1 - DIE_BELOW_SHRINK_MAX);
  const side = dieBelow ? Math.min(sideBelow, sideAbove) : sideAbove;
  const dieSize = dieSlotFor(side);
  const height = side * BOARD_ASPECT;
  const boardLeft = Math.round((stageW - side) / 2);
  const centred = Math.round(boardLeft + (side - dieSize) / 2);
  const footerLiftAt = (top: number): number => Math.max(0, stageH - (top + height + FOOTER_GAP));
  // With the die under the strip, the spare height is shared as before: half above the board
  // (≤ SLACK_ABOVE_MAX), the rest under the die — the board never above the landmark's foot.
  const below = height + underBoard(side);
  const spareBelow = Math.max(0, stageH - (SCENE_HEADER_RESERVE + below));
  const topBelow = Math.min(
    Math.max(
      Math.round(SCENE_HEADER_RESERVE + Math.min(spareBelow * SLACK_ABOVE_SHARE, SLACK_ABOVE_MAX)),
      foot,
    ),
    Math.floor(stageH - below),
  );

  let top: number;
  let die: SceneBox;
  let dieDraw: DieDraw;
  if (dieBelow) {
    top = topBelow;
    die = { left: centred, top: DIE_BELOW_GAP, width: dieSize, height: dieSize };
    // Nothing under it to clear: the usual share, no lift.
    dieDraw = dieDrawFor(dieSize, DIE_VISUAL_GAP_MIN);
  } else {
    // ABOVE the board, as before: half the spare room above it, the die's gap taking half of that —
    // and the board lowered to the foot where the landmark reaches further down.
    // (the spare height as round 3 measured it — under the header strip and the die, not the foot —
    // so the die keeps its gap to the board wherever the foot lowers the board)
    const head = SCENE_HEADER_RESERVE + dieSize + DIE_GAP;
    const slack = Math.max(0, stageH - (head + height + SCENE_GAP_BELOW));
    const above = Math.min(slack * SLACK_ABOVE_SHARE, SLACK_ABOVE_MAX);
    const usual = head + above;
    top = Math.max(Math.round(usual), foot);
    const dieDrop = DIE_GAP + Math.round(above * DIE_GAP_SHARE);
    die = { left: centred, top: top - dieDrop - dieSize, width: dieSize, height: dieSize };
    dieDraw = dieDrawFor(dieSize, dieDrop);
  }
  const board = { left: boardLeft, top, width: side, height };
  const footerLift = footerLiftAt(top);

  if (!pic || side <= 0) {
    return {
      side,
      board,
      die,
      dieBelow,
      dieDraw,
      scale: 0,
      bg: null,
      variant: null,
      sharpness: 0,
      gaps: null,
      fillBox: null,
      feather: null,
      footY: null,
      templeRight: null,
      footerLift,
    };
  }

  const S = pic.scene;
  const variants = pic.bg.variants;
  const pw = S.master.w * s;
  const ph = S.master.h * s;
  // Sideways: a picture that covers the width is NEVER cropped on the left — the temple / pavilion
  // stands at its left edge (the review: a height-bound picture slid toward the 100 cell cut the
  // temple's wall on a tall, narrow screen) — its overhang goes to the right. Only a picture
  // narrower than the scene (capped at 1 : 1) slides toward the 100 cell, inside the scene (P3).
  const sceneLeft = -frame.offX;
  const cell = side / SQUARE;
  const c100 = boardLeft + (FRAME + 0.5) * cell;
  const want = PICTURE_SLIDES_TO_100
    ? c100 - S.band.spireX * s
    : sceneLeft + (frame.sceneW - pw) / 2;
  const picLeft =
    pw >= frame.sceneW
      ? sceneLeft
      : Math.min(Math.max(want, sceneLeft), sceneLeft + frame.sceneW - pw);
  const bg = { left: picLeft, top: picTop, width: pw, height: ph };
  const templeRight = picLeft + S.band.templeRight * s;
  if (!dieBelow) {
    // Centred on the board — unless the temple's box would touch it: then just clear of it.
    die = {
      ...die,
      left: Math.round(
        Math.min(Math.max(centred, templeRight + DIE_TEMPLE_GAP), boardLeft + side - dieSize),
      ),
    };
  }
  const variant = pickVariant(variants, pw * frame.dpr);
  // The fill's box: the scene and the picture together (scene px); its borders reach in to the picture.
  const px = picLeft + frame.offX; // the picture in scene px
  const py = picTop + frame.offY;
  const fx = Math.min(0, px);
  const fy = Math.min(0, py);
  const fillBox = {
    left: fx,
    top: fy,
    width: Math.max(frame.sceneW, px + pw) - fx,
    height: Math.max(frame.sceneH, py + ph) - fy,
  };
  const gaps = {
    top: py - fy,
    right: fillBox.left + fillBox.width - (px + pw),
    bottom: fillBox.top + fillBox.height - (py + ph),
    left: px - fx,
  };
  // The picture's edge fades into the fill only where the fill SHOWS past it inside the scene.
  const shows = {
    top: Math.max(0, py),
    right: Math.max(0, frame.sceneW - (px + pw)),
    bottom: Math.max(0, frame.sceneH - (py + ph)),
    left: Math.max(0, px),
  };
  const fade = (gap: number, most = FEATHER_PX): number =>
    gap > 0.5 ? Math.min(FEATHER_PX, most) : 0;
  return {
    side,
    board,
    die,
    dieBelow,
    dieDraw,
    scale: s,
    bg,
    variant,
    sharpness: variant && pw > 0 ? variant.w / (pw * frame.dpr) : 0,
    gaps,
    fillBox,
    feather: {
      top: fade(shows.top, Math.floor(S.band.top * s)),
      right: fade(shows.right),
      bottom: fade(shows.bottom),
      left: fade(shows.left),
    },
    footY: footAt,
    templeRight,
    footerLift,
  };
}

interface Measured {
  readonly w: number;
  readonly h: number;
  readonly offX: number;
  readonly offY: number;
  readonly sw: number;
  readonly sh: number;
  readonly dpr: number;
}

const same = (a: Measured, b: Measured): boolean =>
  a.w === b.w &&
  a.h === b.h &&
  a.offX === b.offX &&
  a.offY === b.offY &&
  a.sw === b.sw &&
  a.sh === b.sh &&
  a.dpr === b.dpr;

/** The fill's border widths: the picture's gaps, to 0.01 px. */
export function fillBorder(g: SceneGaps): string {
  const px = (v: number): string => `${Math.round(v * 100) / 100}px`;
  return `${px(g.top)} ${px(g.right)} ${px(g.bottom)} ${px(g.left)}`;
}

/** The picture's feather on each side, as the mask class's custom properties. */
function featherVars(f: SceneGaps): CSSProperties {
  return {
    ["--lad-ft" as string]: `${f.top}px`,
    ["--lad-fr" as string]: `${f.right}px`,
    ["--lad-fb" as string]: `${f.bottom}px`,
    ["--lad-fl" as string]: `${f.left}px`,
  } as CSSProperties;
}

export function LaddersScene({
  style,
  renderBoard,
  renderDie,
  footer,
  children,
}: LaddersSceneProps): ReactNode {
  const look = lookFor(style);
  const sceneRef = useRef<HTMLDivElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const [box, setBox] = useState<Measured | null>(null);
  useLayoutEffect(() => {
    const scene = sceneRef.current;
    const stage = stageRef.current;
    if (!scene || !stage) return undefined;
    // ONE observer, two targets: the scene (the screen) and the stage (the footer, the insets). A
    // callback per resize — never a loop; nothing runs while nothing changes.
    const measure = (): void => {
      const a = scene.getBoundingClientRect();
      const b = stage.getBoundingClientRect();
      const next = {
        w: stage.clientWidth,
        h: stage.clientHeight,
        offX: Math.round((b.left - a.left) * 100) / 100,
        offY: Math.round((b.top - a.top) * 100) / 100,
        sw: scene.clientWidth,
        sh: scene.clientHeight,
        dpr:
          typeof window !== "undefined" && window.devicePixelRatio > 0
            ? window.devicePixelRatio
            : 1,
      };
      setBox((prev) => (prev && same(prev, next) ? prev : next));
    };
    measure();
    if (typeof ResizeObserver !== "function") return undefined;
    const ro = new ResizeObserver(measure);
    ro.observe(scene);
    ro.observe(stage);
    return () => ro.disconnect();
  }, []);
  // Laid out once per measure and look — a game's frames re-render the scene, never re-solve it.
  const L = useMemo(
    () =>
      box && box.w > 0 && box.h > 0
        ? sceneLayout(
            {
              stageW: box.w,
              stageH: box.h,
              offX: box.offX,
              offY: box.offY,
              sceneW: box.sw,
              sceneH: box.sh,
              dpr: box.dpr,
            },
            look,
          )
        : null,
    [box, look],
  );
  const fit: LaddersSceneFit = { width: box?.w ?? 0 };
  // NEVER A NARROWER VARIANT once one at least as wide as the box needs is chosen: a later, smaller
  // measure — Telegram reporting its top inset after the first frame of a link-opened table, a
  // Desktop window dragged smaller — would otherwise blank the picture for a fetch of LESS. A wider
  // need steps up; a new look starts afresh — from the preload's file when that one is wide enough.
  const kept = useRef<{ readonly id: string; readonly v: SceneVariant } | null>(null);
  let variant = L?.variant ?? null;
  if (variant) {
    const k = kept.current;
    const held = k && k.id === look.id ? k.v : warmedVariant(look.id);
    if (held && held.w >= variant.w) variant = held;
    kept.current = { id: look.id, v: variant };
  }

  return (
    <div
      ref={sceneRef}
      dir="ltr"
      data-testid="ladders-scene"
      data-style={look.id}
      className={`ladders-scene ladders-scene--${look.kind}`}
      style={{
        position: "relative",
        height: "var(--game-h)",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        // A picture look: its edge colours — the base under the fill (which rides in the bundle,
        // so it is there at once) and the picture (`ScenePicture` fades it in once it has loaded).
        // «اورجینال»: its class's plain backdrop.
        background: look.kind === "picture" ? look.placeholder : undefined,
      }}
    >
      {/* THE FILL: the picture blurred at build time, its edges carried outward — its centre
          stretched exactly under the picture, its margins over every edge past it (the header
          strip, a wider screen's sides). A static border-image: no runtime blur, nothing moves. */}
      {look.kind === "picture" && look.bg.fill && L && L.gaps && L.fillBox ? (
        <div
          data-testid="ladders-fill"
          aria-hidden
          className="ladders-scene__fill"
          style={{
            // Round 5: the scene and the picture together — the picture may run past the scene's
            // right and bottom (cropped there by the scene's overflow), its blurred twin with it.
            position: "absolute",
            left: L.fillBox.left,
            top: L.fillBox.top,
            width: L.fillBox.width,
            height: L.fillBox.height,
            boxSizing: "border-box",
            pointerEvents: "none",
            borderStyle: "solid",
            borderColor: "transparent",
            borderWidth: fillBorder(L.gaps),
            borderImageSource: `url("${look.bg.fill}")`,
            borderImageSlice: `${look.scene.fill.margin} fill`,
            borderImageRepeat: "stretch",
          }}
        />
      ) : null}
      <div
        style={{
          position: "relative",
          flex: "1 1 auto",
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          width: "100%",
          maxWidth: SCENE_COLUMN_MAX,
          margin: "0 auto",
          paddingTop: `calc(${SCENE_PAD_TOP}px + max(env(safe-area-inset-top), var(--tg-safe-top, 0px)))`,
          paddingBottom: "var(--game-safe-bottom)",
          boxSizing: "border-box",
        }}
      >
        <div
          ref={stageRef}
          data-testid="ladders-stage"
          style={{ position: "relative", flex: "1 1 auto", minHeight: 0 }}
        >
          {L ? (
            <>
              {look.kind === "picture" && L.bg && variant && L.feather ? (
                <ScenePicture
                  jpg={variant.jpg}
                  {...(variant.webp ? { webp: variant.webp } : {})}
                  box={L.bg}
                  className="ladders-scene__bg"
                  vars={featherVars(L.feather)}
                  testId="ladders-bg"
                />
              ) : null}
              <div
                data-testid="ladders-board-slot"
                style={{
                  position: "absolute",
                  left: L.board.left,
                  top: L.board.top,
                  width: L.board.width,
                  height: L.board.height,
                }}
              >
                {renderBoard(L.side)}
              </div>
              {/* The die's place: right above the board, centred on it — a clear place of its own —
                  unless it fits under the player strip (round 5: drawn there, in the footer). */}
              {!L.dieBelow ? (
                <div
                  data-testid="ladders-die-place"
                  className="ladders-die-place"
                  style={{
                    position: "absolute",
                    left: L.die.left,
                    top: L.die.top,
                    width: L.die.width,
                    height: L.die.height,
                  }}
                >
                  {renderDie(L.die.width, L.dieDraw)}
                </div>
              ) : null}
            </>
          ) : null}
        </div>
        <div
          data-testid="ladders-footer"
          style={{
            position: "relative",
            flex: "none",
            zIndex: 3,
            // Raised to sit under the board — a static transform, so the measured stage never moves.
            transform: L && L.footerLift > 0 ? `translateY(${-L.footerLift}px)` : undefined,
          }}
        >
          {typeof footer === "function" ? footer(fit) : footer}
          {/* Round 5 (the operator: «bring the die down, so it sits where the thumb reaches more
              easily»): the die's place UNDER the strip — the strip's raise carries it up with it,
              into room sceneLayout proved free (never over the safe bottom inset). */}
          {L && L.dieBelow ? (
            <div
              data-testid="ladders-die-place"
              className="ladders-die-place"
              style={{
                position: "absolute",
                left: L.die.left,
                top: `calc(100% + ${L.die.top}px)`,
                width: L.die.width,
                height: L.die.height,
              }}
            >
              {renderDie(L.die.width, L.dieDraw)}
            </div>
          ) : null}
        </div>
      </div>
      {children}
    </div>
  );
}
